import { RestaurantSource } from '../../services/exports/Constants';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';

// WLP Mixpanel V1 super properties (PRD 5.1), consumer side. Same keys and value types as the
// backend tracker (shop-backend AnalyticsEvent::SUPER_PROPERTIES), so events from the shop, the
// dashboard and the backend segment and join on the same values.

/** Every event from the consumer shop is a customer acting. */
export const CONSUMER_ACTOR_ROLE = 'customer';

/** The Mixpanel group keys. Integer values on every surface, or the groups do not join. */
export const ANALYTICS_GROUP_KEYS = ['partner_id', 'restaurant_id'] as const;

export interface ConsumerSuperProperties {
  /** The white-label partner (service provider) id. Absent for FoodAmigos direct restaurants. */
  partner_id?: number;
  restaurant_id?: number;
  restaurant_source?: RestaurantSource;
  actor_role: typeof CONSUMER_ACTOR_ROLE;
  is_test: boolean;
}

type CompanyContext = Pick<CompanyDetailsInterface, 'id' | 'restaurant_source' | 'service_provider'>;

const asInteger = (value: unknown): number | undefined => {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;

  return Number.isInteger(parsed) ? (parsed as number) : undefined;
};

/**
 * The super properties for the company the customer is ordering from.
 *
 * - `partner_id` only for a partner (`is_master === false`); FoodAmigos direct has none. Unknown
 *   `is_master` (or a payload without the provider id) leaves it out rather than guess.
 * - `restaurant_source` from the company; when the backend does not send it, the master provider's
 *   restaurants are `fa_direct` and a partner's is left out — the consumer payload has no
 *   `partner_type`, so gloriafood_migration and partner cannot be told apart here.
 */
export function buildConsumerSuperProperties(company?: null | CompanyContext): ConsumerSuperProperties {
  const serviceProvider = company?.service_provider;
  const isMaster = serviceProvider?.is_master;

  const partnerId = isMaster === false ? asInteger(serviceProvider?.id) : undefined;
  const restaurantId = asInteger(company?.id);
  const restaurantSource = company?.restaurant_source ?? (isMaster === true ? RestaurantSource.FaDirect : undefined);

  return {
    ...(partnerId !== undefined ? { partner_id: partnerId } : {}),
    ...(restaurantId !== undefined ? { restaurant_id: restaurantId } : {}),
    ...(restaurantSource ? { restaurant_source: restaurantSource } : {}),
    actor_role: CONSUMER_ACTOR_ROLE,
    is_test: serviceProvider?.is_test === true,
  };
}

/** The subset of the Mixpanel SDK this needs, so tests can hand in a fake. */
export interface MixpanelSuperPropertyClient {
  register: (props: Record<string, unknown>) => void;
  unregister: (property: string) => void;
  set_group: (groupKey: string, groupIds: number) => void;
}

const OPTIONAL_KEYS = ['partner_id', 'restaurant_id', 'restaurant_source'] as const;

/**
 * Registers the super properties and the groups on an initialised Mixpanel instance.
 *
 * `set_group` registers its key as a LIST (`restaurant_id: [12]`) and sets it on the people
 * profile; `register` runs after it so the event property is the plain integer the backend sends.
 * A key that no longer applies (the customer moved to a company without a partner, say) is
 * unregistered, or the previous company's value would ride on every later event.
 */
export function applyConsumerSuperProperties(mp: MixpanelSuperPropertyClient, props: ConsumerSuperProperties): void {
  ANALYTICS_GROUP_KEYS.forEach((key) => {
    if (props[key] !== undefined) {
      mp.set_group(key, props[key]);
    }
  });

  OPTIONAL_KEYS.forEach((key) => {
    if (props[key] === undefined) {
      mp.unregister(key);
    }
  });

  mp.register({ ...props });
}
